import { evalAuthHeaders, getEvalBase } from '../../evalApiBase'
import {
  buildRubricsListQuery,
  Rubric,
  RubricCreateInput,
  RubricFilters,
  RubricListResponse,
  RubricUpdateInput,
} from './rubricsAdminModel'

async function readErrorDetail(res: Response): Promise<string | null> {
  try {
    const body = await res.json()
    if (body && typeof body.detail === 'string') {
      return body.detail
    }
  } catch {
    // fall through
  }
  return null
}

export async function listRubrics(
  filters: RubricFilters,
): Promise<RubricListResponse> {
  const query = buildRubricsListQuery(filters)
  const res = await fetch(`${getEvalBase()}/v1/rubrics?${query}`, {
    headers: evalAuthHeaders(),
  })
  if (!res.ok) {
    throw new Error(`Rubrics list failed: ${res.status}`)
  }
  return res.json()
}

export async function createRubric(input: RubricCreateInput): Promise<Rubric> {
  const res = await fetch(`${getEvalBase()}/v1/rubrics`, {
    method: 'POST',
    headers: { ...evalAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  if (!res.ok) {
    // Surface the backend detail — name collisions (409) and empty-body
    // validation failures (422) carry actionable messages.
    const detail = await readErrorDetail(res)
    throw new Error(detail || `Rubric create failed: ${res.status}`)
  }
  return res.json()
}

export async function updateRubric(input: RubricUpdateInput): Promise<Rubric> {
  const { rubric_id, ...payload } = input
  const res = await fetch(`${getEvalBase()}/v1/rubrics/${rubric_id}`, {
    method: 'PUT',
    headers: { ...evalAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!res.ok) {
    // Update is body-only; an empty body returns 422 with an actionable message.
    const detail = await readErrorDetail(res)
    throw new Error(detail || `Rubric update failed: ${res.status}`)
  }
  return res.json()
}

export async function deleteRubric(rubricId: string): Promise<void> {
  const res = await fetch(`${getEvalBase()}/v1/rubrics/${rubricId}`, {
    method: 'DELETE',
    headers: evalAuthHeaders(),
  })
  if (!res.ok) {
    const detail = await readErrorDetail(res)
    throw new Error(detail || `Rubric delete failed: ${res.status}`)
  }
}
